import React, { useRef, useState } from 'react';
import { Modal, Button, Typography, Box, Stack, Chip, Divider, IconButton } from '@mui/material';
import { Close, CloudUpload } from '@mui/icons-material';
import { apis } from 'utils/lib/apis';
import { message } from 'antd';

export default function UploadCsvModal({ open, onClose, onSubmit, onSuccess }) {
  const [firstFile, setFirstFile] = useState(null);
  const [secondFile, setSecondFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [errorText, setErrorText] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const normalizeName = (f) => String(f?.name || '').trim().toLowerCase();
  const isCourses = (n) => n.endsWith('courses.csv');
  const isSections = (n) => n.endsWith('sections.csv');
  const isAllowed = (f) => {
    const n = normalizeName(f);
    return isCourses(n) || isSections(n);
  };
  const bothValid = Boolean(
    firstFile &&
    secondFile &&
    isAllowed(firstFile) &&
    isAllowed(secondFile) &&
    ((isCourses(normalizeName(firstFile)) && isSections(normalizeName(secondFile))) ||
     (isSections(normalizeName(firstFile)) && isCourses(normalizeName(secondFile))))
  );

  const handleFiles = (files) => {
    setErrorText('');
    const arr = Array.from(files || []);
    const valid = arr.filter((f) => isAllowed(f));
    if (valid.length === 0) {
      setErrorText('Only files ending with courses.csv or sections.csv are allowed');
      return;
    }
    let c = isCourses(normalizeName(firstFile || {})) ? firstFile : null;
    let s = isSections(normalizeName(firstFile || {})) ? firstFile : null;
    if (secondFile) {
      if (isCourses(normalizeName(secondFile))) c = secondFile;
      if (isSections(normalizeName(secondFile))) s = secondFile;
    }
    valid.forEach((f) => {
      const n = normalizeName(f);
      if (isCourses(n)) c = f;
      if (isSections(n)) s = f;
    });
    setFirstFile(c);
    setSecondFile(s);
  };

  return (
    <Modal open={open} onClose={() => {
      setFirstFile(null);
      setSecondFile(null);
      setErrorText('');
      onClose();
    }}>
      <Box
        sx={{
          left: '50%',
          maxWidth: 560,
          width: '92vw',
          position: 'absolute',
          borderRadius: 12,
          backgroundColor: '#fff',
          p: 3,
          transform: 'translate(-50%, -50%)',
          top: '50%',
          boxShadow: '0 10px 30px rgba(0,0,0,0.15)'
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="h6">Upload Course Info</Typography>
          <IconButton onClick={() => {
            setFirstFile(null);
            setSecondFile(null);
            setErrorText('');
            onClose();
          }} disabled={submitting}>
            <Close />
          </IconButton>
        </Stack>
        <Typography variant="body2" sx={{ color: '#555', mb: 2 }}>
          Select two files: one ending with courses.csv and one ending with sections.csv
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Stack spacing={2}>
          <Box
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setDragActive(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setDragActive(false);
              handleFiles(e.dataTransfer.files);
            }}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            sx={{
              border: '2px dashed #94a3b8',
              borderRadius: 10,
              p: 3,
              textAlign: 'center',
              backgroundColor: dragActive ? '#f0f9ff' : '#f9fafb',
              cursor: 'pointer',
              transition: 'all .15s ease-in-out'
            }}
          >
            <Stack spacing={1} alignItems="center">
              <CloudUpload sx={{ fontSize: 36, color: '#2563eb' }} />
              <Typography variant="subtitle1">Drag & drop files here</Typography>
              <Typography variant="body2" sx={{ color: '#64748b' }}>or click to browse .csv files</Typography>
              <Typography variant="caption" sx={{ color: '#64748b' }}>Need exactly one courses.csv and one sections.csv</Typography>
            </Stack>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => handleFiles(e.target.files)}
            />
          </Box>
          <Stack spacing={1}>
            <Typography variant="subtitle2">Selected files</Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
              {firstFile && (
                <Chip label={firstFile.name} onDelete={() => setFirstFile(null)} size="small" />
              )}
              {secondFile && (
                <div style={{ marginTop: '10px', marginLeft: '0px' }}>
                  <Chip label={secondFile.name} onDelete={() => setSecondFile(null)} size="small" />
                </div>
              )}
              {!firstFile && (
                <Chip label="courses.csv" variant="outlined" size="small" />
              )}
              {!secondFile && (
                <Chip label="sections.csv" variant="outlined" size="small" />
              )}
            </Stack>
          </Stack>
          {errorText ? (
            <Typography variant="body2" sx={{ color: '#d32f2f' }}>{errorText}</Typography>
          ) : null}
          <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 1 }}>
            <Button onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button
              variant="contained"
              disabled={!bothValid || submitting}
              onClick={async () => {
                setSubmitting(true);
                try {
                  const toBase64 = (file) => new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => {
                      const result = String(reader.result || '');
                      const base64 = result.includes(',') ? result.split(',')[1] : result;
                      resolve(base64);
                    };
                    reader.onerror = (e) => reject(e);
                    reader.readAsDataURL(file);
                  });
                  const [a64, b64] = await Promise.all([
                    toBase64(firstFile),
                    toBase64(secondFile)
                  ]);
                  const response = await apis.post('clever', `/clever/csv/parse`, {
                    body: {
                      files: [
                        { filename: firstFile.name, isBase64: true, content: a64 },
                        { filename: secondFile.name, isBase64: true, content: b64 }
                      ]
                    }
                  });
                  if (response.status_code === 200) {
                    message.success('Course info uploaded successfully');
                  } else {
                    message.error(`Something went wrong`)
                  }
                  if (onSuccess) onSuccess(response);
                  if (onSubmit) onSubmit({ firstFile, secondFile, response });
                  setFirstFile(null);
                  setSecondFile(null);
                  onClose && onClose();
                } catch (e) {
                  console.error('CSV parse failed', e);
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
}


